import { EmptyState, MEyebrow, PlatformIcon } from "@cntrl/ui";
import { useDashboardStats } from "../../../app/hooks/useDashboardStats.js";
import { CardError, CardLoading } from "./mobileCard.js";
import { num } from "./homeFormat.js";
import styles from "./MobileHome.module.scss";

/** The socials platforms the Audience grid renders, in order (ART-181). Spotify leads as its own wide card. */
const SOCIAL_ORDER = ["instagram", "facebook", "tiktok", "youtube"] as const;

const PLATFORM_LABEL: Record<string, string> = {
  spotify: "Spotify",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
};

/** "3d ago" / "today" from a date-time stamp (whole days, wall-clock enough for a last-post age). */
function agoLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  return `${days}d ago`;
}

/**
 * AUDIENCE (ART-181) — the streaming + socials section that was missing from the mobile home. Not one card
 * but a titled SECTION: a wide Spotify card (monthly listeners | followers) over a 2-col grid of the socials
 * (Instagram carries its last-post age). Reads the money-free analytics doc, so it rides releases:read at the
 * nav document exactly as the retired `your_numbers` card did.
 *
 * A platform with no tile is simply absent (a band connects what it connects); a followers figure we do not
 * hold renders the em-dash via {@link num}, never a fabricated 0.
 */
export function AudienceCard() {
  const q = useDashboardStats();
  const spotify = q.data?.streaming?.spotify ?? null;
  const tiles = q.data?.socials?.tiles ?? [];
  const byPlatform = new Map(tiles.filter((t) => t.platform).map((t) => [t.platform!, t]));
  const socialCards = SOCIAL_ORDER.filter((p) => byPlatform.has(p));

  const hasSpotify = spotify?.monthlyListeners != null || spotify?.followers != null;
  const anyData = hasSpotify || socialCards.length > 0;

  return (
    <section className={styles.audience} aria-label="Audience">
      <header className={styles.cardHead}>
        <MEyebrow>Audience</MEyebrow>
      </header>
      {q.isLoading ? (
        <CardLoading />
      ) : q.isError ? (
        <CardError error={q.error} onRetry={() => void q.refetch()} />
      ) : !anyData ? (
        <EmptyState title="No audience yet" description="Connect your platforms to see streaming and socials." />
      ) : (
        <div className={styles.audienceStack}>
          {hasSpotify && (
            <div className={styles.audienceWide}>
              <p className={styles.audienceHead}>
                {/* Decorative — the platform name sits right beside it, so the icon must not double-announce. */}
                <PlatformIcon platform="spotify" size={20} />
                <span>{PLATFORM_LABEL.spotify}</span>
              </p>
              <div className={styles.audienceSplit}>
                <div className={styles.audienceStat}>
                  <span className={styles.audienceValue}>{num(spotify?.monthlyListeners)}</span>
                  <span className={styles.audienceLabel}>Monthly listeners</span>
                </div>
                <div className={styles.audienceStat}>
                  <span className={styles.audienceValue}>{num(spotify?.followers)}</span>
                  <span className={styles.audienceLabel}>Followers</span>
                </div>
              </div>
            </div>
          )}
          {socialCards.length > 0 && (
            <div className={styles.audienceGrid}>
              {socialCards.map((p) => {
                const tile = byPlatform.get(p)!;
                const ago = agoLabel(tile.lastPostOn);
                return (
                  <div key={p} className={styles.audienceTile}>
                    <p className={styles.audienceHead}>
                      <PlatformIcon platform={p} size={16} />
                      <span>{PLATFORM_LABEL[p] ?? p}</span>
                    </p>
                    <span className={styles.audienceValue}>{num(tile.followers)}</span>
                    {ago && <span className={styles.audienceLabel}>Last post · {ago}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
